import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'what-it-is', title: '1. What Document Studio is' },
  { id: 'document-types', title: '2. Document types' },
  { id: 'generating', title: '3. Generating a document' },
  { id: 'templates', title: '4. Templates' },
  { id: 'refining', title: '5. Refining & exporting' },
  { id: 'quality', title: '6. Getting good output' },
];

const DocumentStudioPage = () => {
  return (
    <>
      <NextSeo
        title="Document Studio"
        description="Generate long-form, cited documents from a chatbot's knowledge base — memoranda, reports, policies and analyses — then refine and export them to Word or PDF."
        canonical="https://chat.co/docs/document-studio"
        openGraph={{
          url: 'https://chat.co/docs/document-studio',
          title: 'Document Studio | Chat.co',
          description:
            'Generate long-form, cited documents from a chatbot’s knowledge base, then refine and export to Word or PDF.',
          type: 'article',
          article: {
            publishedTime: '2026-08-30T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Operations',
            tags: ['document generation', 'reports', 'docx', 'drafting'],
          },
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Operations"
        categoryColor="text-blue-700"
        title="Document Studio"
        description="Long-form documents built from your own knowledge base, with citations back to the source — not a chat answer stretched to ten pages."
        readingTime="9 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Analytics, ROI & knowledge gaps',
          href: PageLink.docsAnalyticsRoi,
        }}
        nextArticle={{
          title: 'Managing multiple bots & teams',
          href: PageLink.docsMultiBotOrchestration,
        }}
        keyTakeaways={[
          'Documents are generated per bot, grounded in that bot’s knowledge base',
          'Eleven document types ship with the product, each with its own section structure and citation style',
          'You can quote a document before generating it, so cost is never a surprise',
          'Templates let you fix your own section structure — up to twelve sections',
          'Finished documents export to Word (.docx) and PDF',
        ]}>
        <p className="lead">
          Chat answers a question. Document Studio produces a deliverable: a structured, sectioned,
          cited document built from the same knowledge base — the memo, the report, the policy, the
          filing. It is the difference between asking your corpus a question and asking it to write
          something down.
        </p>

        <section id="what-it-is" className="scroll-mt-28">
          <h2>1. What Document Studio is</h2>
          <p>
            Open a bot and choose <strong>Documents</strong> under Interactions. You give it a
            document type, a target length and a prompt; it plans sections, drafts each one against
            retrieved evidence, and assembles the result with citations pointing back to the source
            material.
          </p>

          <DocsCardGrid columns={3}>
            <DocsCard icon="tabler:layout-list" title="Structured">
              Real sections with stated objectives, not a wall of generated prose.
            </DocsCard>
            <DocsCard icon="tabler:quote" title="Cited">
              Numbered footnotes or inline references, depending on the document type.
            </DocsCard>
            <DocsCard icon="tabler:file-download" title="Exportable">
              Word and PDF, ready to send.
            </DocsCard>
          </DocsCardGrid>

          <DocsCallout variant="note" title="It is only as good as the corpus">
            Document Studio retrieves from the bot&apos;s knowledge base and nowhere else. A thin
            corpus produces a thin document — longer, but no better informed. If the material is not
            in the knowledge base, generating a longer document will not conjure it.
          </DocsCallout>
        </section>

        <section id="document-types" className="scroll-mt-28">
          <h2>2. Document types</h2>
          <p>
            The type is not cosmetic. It sets the default section structure, the tone, and whether
            citations render as numbered footnotes or inline references.
          </p>

          <DocsTable
            headers={['Type', 'Shape']}
            rows={[
              [
                <strong key="1">Report</strong>,
                'General-purpose structured report — the sensible default',
              ],
              [
                <strong key="2">Regulatory memorandum</strong>,
                'Formal memo with footnoted citations',
              ],
              [<strong key="3">Opinion paper</strong>, 'Position and argument, sourced'],
              [<strong key="4">Docket summary</strong>, 'Condensed account of a proceeding'],
              [
                <strong key="5">Comparative analysis</strong>,
                'Structured comparison across options or sources',
              ],
              [<strong key="6">Flash report</strong>, 'Short dashboard-style summary'],
              [
                <strong key="7">Security policy</strong>,
                'Policy document in the conventional shape',
              ],
              [<strong key="8">Security procedure</strong>, 'Step-level operational procedure'],
              [<strong key="9">PREB draft order</strong>, 'Regulatory draft order'],
              [
                <strong key="10">DCPSC outage compliance filing</strong>,
                'Jurisdiction-specific compliance filing',
              ],
              [
                <strong key="11">HIPAA self-assessment report</strong>,
                'Structured self-assessment output',
              ],
            ]}
          />

          <p>
            The last few are narrow by design — they exist because specific regulated workflows
            needed them. If none fits, <strong>Report</strong> plus a template (section 4) will get
            you closer than forcing a mismatched type.
          </p>
        </section>

        <section id="generating" className="scroll-mt-28">
          <h2>3. Generating a document</h2>

          <DocsSteps>
            <DocsStep title="Pick the type and the length">
              <p>
                Target length runs from one to ten pages. Ask for what the content supports, not for
                what sounds impressive — a padded ten-page document reads worse than a dense
                four-page one.
              </p>
            </DocsStep>
            <DocsStep title="Write the prompt">
              <p>
                This is the brief, and it is where most of the quality comes from. Say who the
                reader is, what decision the document supports, and what must be covered. Section 6
                goes into this properly.
              </p>
            </DocsStep>
            <DocsStep title="Optionally seed it from a conversation">
              <p>
                You can attach specific chat messages as verbatim source material — useful when you
                have already worked something out in chat and want it written up rather than
                rediscovered.
              </p>
            </DocsStep>
            <DocsStep title="Get a quote">
              <p>
                Before generating, request a quote for the cost of the run. Longer documents cost
                more; the quote means you find out before rather than after.
              </p>
            </DocsStep>
            <DocsStep title="Generate">
              <p>
                Output streams as it is produced, so you can see the shape early and cancel if the
                brief was wrong — rather than waiting for a finished document you do not want.
              </p>
            </DocsStep>
          </DocsSteps>
        </section>

        <section id="templates" className="scroll-mt-28">
          <h2>4. Templates</h2>
          <p>
            When you produce the same kind of document repeatedly, fix its structure once. A
            template holds:
          </p>

          <DocsTable
            headers={['Field', 'What it controls']}
            rows={[
              [
                <strong key="a">Name &amp; description</strong>,
                'How you and your team recognise it',
              ],
              [<strong key="b">Document type</strong>, 'The base shape it builds on'],
              [
                <strong key="c">Section blueprints</strong>,
                'Up to twelve sections, each with a title and a stated objective — the objective is what the drafter actually works to',
              ],
              [<strong key="d">Citation style</strong>, 'Numbered footnotes or inline references'],
              [
                <strong key="e">Prompt guidance</strong>,
                'Standing instructions applied to every document from this template — tone, conventions, things to always or never do',
              ],
            ]}
          />

          <DocsCallout variant="tip" title="Write objectives, not headings">
            &ldquo;Analysis&rdquo; as a section objective produces generic analysis. &ldquo;Compare
            the two proposals against the stated evaluation criteria and identify where they
            diverge&rdquo; produces the document you wanted. The objective field is doing the real
            work — treat it as a brief for that section.
          </DocsCallout>
        </section>

        <section id="refining" className="scroll-mt-28">
          <h2>5. Refining &amp; exporting</h2>
          <p>
            A generated document is not final. You can issue a refinement instruction against it —
            &ldquo;tighten the executive summary&rdquo;, &ldquo;add a paragraph on the cost
            implications&rdquo;, &ldquo;make the tone more formal&rdquo; — and it revises in place,
            streaming as it goes.
          </p>

          <p>
            Refinement is usually the better move when a document is 80% right. Regenerating from
            scratch throws away the parts that worked and rolls the dice again.
          </p>

          <h3>Export</h3>
          <p>Finished documents download as:</p>
          <ul>
            <li>
              <strong>Word (.docx)</strong> — when someone will edit it further
            </li>
            <li>
              <strong>PDF</strong> — when it is going out as-is
            </li>
          </ul>

          <p>Both carry the document&apos;s formatting and citations.</p>
        </section>

        <section id="quality" className="scroll-mt-28">
          <h2>6. Getting good output</h2>

          <h3>Brief it like a person</h3>
          <p>
            The prompt is the strongest lever you have. Compare &ldquo;write a report on our
            security posture&rdquo; with: &ldquo;Write a report for the board summarising our
            security posture as of Q3, covering the control gaps identified in the last assessment,
            what has been remediated since, and what remains open with owners and dates. Assume the
            reader is not technical.&rdquo; The second produces a usable document; the first
            produces filler.
          </p>

          <h3>Check the citations, not just the prose</h3>
          <p>
            Generated text reads fluently whether or not it is well grounded — fluency is exactly
            what these models are best at. The citations are how you tell. Open a few and confirm
            they support the sentence they are attached to. If citations are thin or wrong, the
            problem is the knowledge base, and{' '}
            <Link href={PageLink.docsCitationQuality}>citation quality</Link> covers the fixes.
          </p>

          <h3>Feed it the corpus before you ask for the document</h3>
          <p>
            The most common disappointment is a document generated over a knowledge base that never
            contained the source material. Load what the document should be built from — see the{' '}
            <Link href={PageLink.docsKnowledgeUpload}>knowledge upload playbook</Link> — and then
            ask.
          </p>

          <h3>Do not ship it unread</h3>
          <p>
            Everything here produces a strong draft, and the whole point is that a strong draft
            saves hours. It is still a draft. Read it as its author, because on anything that leaves
            your organisation, you are.
          </p>

          <DocsCardGrid columns={2}>
            <DocsCard
              icon="tabler:upload"
              title="Knowledge upload playbook"
              href={PageLink.docsKnowledgeUpload}>
              Get the source material in before you generate.
            </DocsCard>
            <DocsCard
              icon="tabler:quote"
              title="Citation quality"
              href={PageLink.docsCitationQuality}>
              When the citations do not support the claims.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default DocumentStudioPage;
